import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Target, FlaskConical, GraduationCap, Handshake, Rocket,
  Megaphone, Search, CalendarDays, Mail, Users, BookOpen, Zap,
  Globe, Building2, CheckCircle2, ArrowRight
} from 'lucide-react';
import './CommercialPrograms.css';

const services = [
  {
    icon: FlaskConical,
    color: 'teal',
    title: 'Paid Research',
    description:
      'Commission custom quantum research projects. We design and execute engagements tailored to your specific industry challenges — from optimization problems to post-quantum cryptography.',
    tags: ['Applied Quantum', 'Industry Research', 'Reports & Publications'],
  },
  {
    icon: Target,
    color: 'blue',
    title: 'Consulting & Advisory',
    description:
      'Navigate the quantum landscape with confidence. Our advisory services cover quantum readiness roadmaps, technology selection guidance, and end-to-end implementation strategy.',
    tags: ['Quantum Readiness', 'Strategy', 'Implementation'],
  },
  {
    icon: Megaphone,
    color: 'purple',
    title: 'Outreach & Awareness Campaigns',
    description:
      'Amplify your quantum story. We design and run paid awareness campaigns reaching scientists, engineers, policymakers, and the public across Kenya and Africa.',
    tags: ['Brand Awareness', 'Public Engagement', 'Media Campaigns'],
  },
  {
    icon: GraduationCap,
    color: 'green',
    title: 'Enterprise Training',
    description:
      'Prepare your team for the quantum era. Our tailored corporate training programs range from quantum fundamentals to applied quantum computing, delivered by certified instructors.',
    tags: ['Corporate Bootcamps', 'Workshops', 'Certification'],
  },
  {
    icon: Rocket,
    color: 'orange',
    title: 'Startup Support',
    description:
      'Turn quantum ideas into viable ventures. We support quantum startups with go-to-market strategy, investor connections, technical mentorship, and access to our broader network.',
    tags: ['Incubation', 'Mentorship', 'Investor Connect'],
  },
  {
    icon: Handshake,
    color: 'teal',
    title: 'Industry Partnerships',
    description:
      'Build strategic bridges between academia, industry, and government. We facilitate structured partnerships that accelerate quantum technology adoption and commercialization.',
    tags: ['Public-Private', 'Joint Ventures', 'Policy Engagement'],
  },
  {
    icon: Search,
    color: 'blue',
    title: 'Quantum Readiness Assessment',
    description:
      'Understand exactly where your organization stands relative to quantum disruption. We provide detailed assessments with a prioritized, actionable improvement roadmap.',
    tags: ['Gap Analysis', 'Risk Assessment', 'Roadmap'],
  },
  {
    icon: CalendarDays,
    color: 'purple',
    title: 'Sponsored Events & Hackathons',
    description:
      'Commission and co-brand quantum events, hackathons, and challenges. We handle everything from concept to execution, delivering high-impact outcomes for your brand and community.',
    tags: ['Hackathons', 'Conferences', 'Branded Programs'],
  },
];

const steps = [
  {
    number: '01',
    color: 'teal',
    icon: Mail,
    title: 'Reach Out',
    description:
      "Send us an inquiry at programs@quantumke.org describing your organization and what you're looking to accomplish.",
  },
  {
    number: '02',
    color: 'blue',
    icon: Users,
    title: 'Discovery Call',
    description:
      "We'll schedule a call to deeply understand your needs, challenges, and goals before proposing anything.",
  },
  {
    number: '03',
    color: 'purple',
    icon: BookOpen,
    title: 'Tailored Proposal',
    description:
      'Our team designs a custom engagement plan with clear deliverables, timelines, and pricing aligned to your context.',
  },
  {
    number: '04',
    color: 'green',
    icon: Zap,
    title: 'Delivery & Impact',
    description:
      'We execute with rigor, measure outcomes, and provide full reporting so you can see the real-world value created.',
  },
];

const differentiators = [
  {
    icon: Globe,
    title: 'Africa-Rooted Expertise',
    description:
      "We combine deep quantum science knowledge with unparalleled understanding of the African tech ecosystem, regulatory environment, and talent landscape — giving you a partner who actually knows the terrain.",
  },
  {
    icon: Users,
    title: 'Community-Backed Delivery',
    description:
      'Every commercial engagement draws on our 500+ member network of researchers, engineers, and practitioners. You get a team, not just a consultant.',
  },
  {
    icon: Building2,
    title: 'Globally Connected',
    description:
      'We maintain active links with IBM Quantum, QWorld, CERN, and leading academic institutions worldwide — ensuring world-class standards underpin every local engagement.',
  },
];

const ServicesExplorer = () => {
  const [active, setActive] = useState(0);
  const service = services[active];
  const Icon = service.icon;

  return (
    <div className="svc-explorer">
      <div className="svc-tab-list">
        {services.map((s, i) => {
          const TabIcon = s.icon;
          return (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`svc-tab ${i === active ? `svc-tab-active svc-tab-${s.color}` : ''}`}
            >
              <span className="svc-tab-num">{String(i + 1).padStart(2, '0')}</span>
              <div className={`svc-tab-icon-wrap svc-icon-${s.color}`}>
                <TabIcon className="svc-tab-icon" />
              </div>
              <span className="svc-tab-name">{s.title}</span>
              <ArrowRight className="svc-tab-arrow" size={13} />
            </button>
          );
        })}
      </div>

      <div className={`svc-detail svc-detail-${service.color}`}>
        <div className="svc-detail-glow" />
        <div className="svc-detail-bg-num">{String(active + 1).padStart(2, '0')}</div>
        <div className="svc-detail-body">
          <div className={`svc-detail-icon-ring svc-icon-${service.color}`}>
            <Icon className="svc-detail-icon" />
          </div>
          <h3 className="svc-detail-title">{service.title}</h3>
          <p className="svc-detail-desc">{service.description}</p>
          <div className="svc-detail-tags">
            {service.tags.map((tag, j) => (
              <span key={j} className={`svc-detail-tag svc-dtag-${service.color}`}>{tag}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const CommercialPrograms = () => (
  <div className="commercial-page">
    <Helmet>
      <title>Commercial Programs | QuantumKE</title>
      <meta name="description" content="QuantumKE's commercial programs offer paid quantum research, consulting, training, and partnerships for organizations in Kenya and Africa." />
      <link rel="canonical" href="https://quantumke.org/commercial-programs" />
    </Helmet>

    {/* ========== BANNER ========== */}
    <section className="commercial-banner">
      <div className="commercial-banner-overlay" />
      <div className="commercial-banner-content">
        <h1 className="commercial-banner-title">Commercial Programs</h1>
        <p className="commercial-banner-subtitle">
          Turning quantum knowledge into real-world value — for enterprises, governments, and innovators across Africa.
        </p>
      </div>
    </section>

    {/* ========== INTRO ========== */}
    <section className="commercial-intro-section">
      <div className="commercial-container">
        <div className="commercial-intro-content">

          <div className="commercial-intro-text">
            <span className="commercial-label">Why Commercial Programs?</span>
            <h2 className="commercial-intro-title">
              Commercializing Quantum for{' '}
              <span className="commercial-gradient-text">Africa's Growth</span>
            </h2>
            <p className="commercial-intro-desc">
              QuantumKE is more than a learning community — we're a full-spectrum quantum ecosystem.
              Our commercial programs translate cutting-edge quantum research and expertise into tangible
              business value. Whether you need research, consulting, training, or awareness campaigns,
              we design engagements that drive real, measurable outcomes.
            </p>
            <p className="commercial-intro-desc">
              Backed by a 500+ member community of researchers, engineers, and industry professionals,
              our commercial offerings are grounded in genuine expertise — not theoretical consulting.
            </p>
            <a href="mailto:programs@quantumke.org" className="commercial-intro-cta">
              Start a Conversation <ArrowRight size={16} />
            </a>
          </div>

          <div className="commercial-intro-card">
            <span className="commercial-feature-badge">What We Offer</span>
            <ul className="commercial-feature-list">
              {[
                'Paid Quantum Research Engagements',
                'Consulting & Strategic Advisory',
                'Outreach & Awareness Campaigns',
                'Enterprise Talent Development',
                'Startup Support & Incubation',
                'Quantum Readiness Assessments',
                'Sponsored Events & Hackathons',
                'Industry Partnership Facilitation',
              ].map((item, i) => (
                <li key={i}>
                  <CheckCircle2 className="commercial-check-icon" />
                  {item}
                </li>
              ))}
            </ul>
            <div className="commercial-feature-footer">
              <div className="commercial-stats-row">
                <div className="commercial-stat">
                  <span className="commercial-stat-value">500+</span>
                  <span className="commercial-stat-label">Community Members</span>
                </div>
                <div className="commercial-stat">
                  <span className="commercial-stat-value">15+</span>
                  <span className="commercial-stat-label">Industry Partners</span>
                </div>
                <div className="commercial-stat">
                  <span className="commercial-stat-value">20+</span>
                  <span className="commercial-stat-label">Programs Run</span>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>

    {/* ========== SERVICES ========== */}
    <section className="commercial-services-section">
      <div className="commercial-container">
        <div className="commercial-section-header">
          <span className="commercial-section-eyebrow">Our Offerings</span>
          <h2 className="commercial-section-title">What We Offer</h2>
          <p className="commercial-section-description">
            Eight commercial tracks designed to meet organizations wherever they are in their quantum journey.
          </p>
        </div>
        <ServicesExplorer />
      </div>
    </section>

    {/* ========== HOW TO ENGAGE ========== */}
    <section className="commercial-process-section">
      <div className="commercial-container">
        <div className="commercial-section-header">
          <span className="commercial-section-eyebrow">The Process</span>
          <h2 className="commercial-section-title">How to Engage</h2>
          <p className="commercial-section-description">
            A simple, transparent process from first contact to measurable impact.
          </p>
        </div>
        <div className="commercial-timeline">
          <div className="commercial-timeline-track" />
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <div key={i} className="commercial-timeline-step">
                <div className={`commercial-timeline-node commercial-tnode-${step.color}`}>
                  <span className="commercial-timeline-node-num">{step.number}</span>
                  <Icon className="commercial-timeline-node-icon" />
                </div>
                <div className="commercial-timeline-content">
                  <h3 className="commercial-timeline-title">{step.title}</h3>
                  <p className="commercial-timeline-desc">{step.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* ========== WHY US ========== */}
    <section className="commercial-why-section">
      <div className="commercial-container">
        <div className="commercial-section-header">
          <h2 className="commercial-section-title">Why Work With QuantumKE?</h2>
          <p className="commercial-section-description">
            We're not a traditional consultancy. We're a community that happens to be deeply expert in quantum.
          </p>
        </div>
        <div className="commercial-why-grid">
          {differentiators.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} className="commercial-why-card">
                <div className="commercial-why-icon-wrap">
                  <Icon className="commercial-why-icon" />
                </div>
                <h3 className="commercial-why-title">{d.title}</h3>
                <p className="commercial-why-desc">{d.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>


  </div>
);

export default CommercialPrograms;
